import { es } from "date-fns/locale/es";
import { formatInTimeZone } from "date-fns-tz/formatInTimeZone";
import { fromZonedTime } from "date-fns-tz/fromZonedTime";
import { toZonedTime } from "date-fns-tz/toZonedTime";
export const ARGENTINA_TZ = "America/Argentina/Buenos_Aires";
export const DEFAULT_FROM_DATE = new Date("2000-01-01");
const URL_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const DATE_ONLY_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;
const COMPACT_DATE_ONLY_REGEX = /^(\d{4})(\d{2})(\d{2})$/;
const UTC_MIDNIGHT_TIMESTAMP_REGEX = /^(\d{4}-\d{2}-\d{2})T00:00(?::00(?:\.\d{1,3})?)?(?:Z|[+-]00:00)$/i;
const ARGENTINA_LOCAL_DATETIME_REGEX = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?)$/;
const DAY_IN_MS = 1000 * 60 * 60 * 24;
const UTC_TZ = "UTC";
const ARGENTINA_WEEK_START_DAY = 1;
function getDaysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}
function formatDateParts({ year, month, day }) {
    return `${year}-${`${month}`.padStart(2, "0")}-${`${day}`.padStart(2, "0")}`;
}
function parseDateParts(dateStr) {
    const [yearString, monthString, dayString] = dateStr.split("-");
    const year = Number.parseInt(yearString ?? "", 10);
    const month = Number.parseInt(monthString ?? "", 10);
    const day = Number.parseInt(dayString ?? "", 10);
    return { year, month, day };
}
function getArgentinaDateParts(date) {
    if (typeof date === "string" && isValidUrlDate(date)) {
        return parseDateParts(date);
    }
    return parseDateParts(formatDateForUrl(new Date(date)));
}
function getArgentinaDayNumber(date) {
    const { year, month, day } = getArgentinaDateParts(date);
    return Math.trunc(Date.UTC(year, month - 1, day) / DAY_IN_MS);
}
function getDateFromDayNumber(dayNumber) {
    const date = new Date(dayNumber * DAY_IN_MS);
    return {
        year: date.getUTCFullYear(),
        month: date.getUTCMonth() + 1,
        day: date.getUTCDate(),
    };
}
function fromArgentinaDayNumber(dayNumber) {
    return parseArgentinaDateAtStartOfDay(formatDateParts(getDateFromDayNumber(dayNumber)));
}
function addMonthsToParts({ year, month, day }, months) {
    const zeroBasedMonth = month - 1 + months;
    const targetYear = year + Math.floor(zeroBasedMonth / 12);
    const targetMonth = ((zeroBasedMonth % 12) + 12) % 12;
    const targetMonthNumber = targetMonth + 1;
    const targetDay = Math.min(day, getDaysInMonth(targetYear, targetMonthNumber));
    return {
        year: targetYear,
        month: targetMonthNumber,
        day: targetDay,
    };
}
function getDayNumberInTimeZone(date, timeZone) {
    const dateInZone = formatInTimeZone(date, timeZone, "yyyy-MM-dd");
    const [yearString, monthString, dayString] = dateInZone.split("-");
    const year = Number.parseInt(yearString ?? "", 10);
    const month = Number.parseInt(monthString ?? "", 10);
    const day = Number.parseInt(dayString ?? "", 10);
    return Math.trunc(Date.UTC(year, month - 1, day) / DAY_IN_MS);
}
export function isUtcMidnight(date) {
    return (date.getUTCHours() === 0 &&
        date.getUTCMinutes() === 0 &&
        date.getUTCSeconds() === 0 &&
        date.getUTCMilliseconds() === 0);
}
export function formatDateForUrl(date) {
    return formatInTimeZone(date, ARGENTINA_TZ, "yyyy-MM-dd", { locale: es });
}
export function startOfArgentinaDay(date) {
    if (typeof date === "string" && isValidUrlDate(date)) {
        return parseArgentinaDateAtStartOfDay(date);
    }
    return parseArgentinaDateAtStartOfDay(formatDateForUrl(new Date(date)));
}
export function addArgentinaDays(date, days) {
    return fromArgentinaDayNumber(getArgentinaDayNumber(date) + days);
}
export function addArgentinaMonths(date, months) {
    return parseArgentinaDateAtStartOfDay(formatDateParts(addMonthsToParts(getArgentinaDateParts(date), months)));
}
export function startOfArgentinaWeek(date) {
    const dayNumber = getArgentinaDayNumber(date);
    const utcDay = new Date(dayNumber * DAY_IN_MS).getUTCDay();
    const offset = (utcDay - ARGENTINA_WEEK_START_DAY + 7) % 7;
    return fromArgentinaDayNumber(dayNumber - offset);
}
export function endOfArgentinaWeek(date) {
    return addArgentinaDays(startOfArgentinaWeek(date), 6);
}
export function startOfArgentinaMonth(date) {
    const { year, month } = getArgentinaDateParts(date);
    return parseArgentinaDateAtStartOfDay(formatDateParts({ year, month, day: 1 }));
}
export function endOfArgentinaMonth(date) {
    const { year, month } = getArgentinaDateParts(date);
    return parseArgentinaDateAtStartOfDay(formatDateParts({ year, month, day: getDaysInMonth(year, month) }));
}
export function startOfArgentinaQuarter(date) {
    const { year, month } = getArgentinaDateParts(date);
    const quarterStartMonth = Math.floor((month - 1) / 3) * 3 + 1;
    return parseArgentinaDateAtStartOfDay(formatDateParts({ year, month: quarterStartMonth, day: 1 }));
}
export function endOfArgentinaQuarter(date) {
    return addArgentinaDays(addArgentinaMonths(startOfArgentinaQuarter(date), 3), -1);
}
export function startOfArgentinaYear(date) {
    const { year } = getArgentinaDateParts(date);
    return parseArgentinaDateAtStartOfDay(formatDateParts({ year, month: 1, day: 1 }));
}
export function endOfArgentinaYear(date) {
    const { year } = getArgentinaDateParts(date);
    return parseArgentinaDateAtStartOfDay(formatDateParts({ year, month: 12, day: 31 }));
}
export function parseUrlDate(dateStr) {
    if (!dateStr) {
        return new Date(Number.NaN);
    }
    const [yearString, monthString, dayString] = dateStr.split("-");
    const year = Number.parseInt(yearString ?? "", 10);
    const month = Number.parseInt(monthString ?? "", 10);
    const day = Number.parseInt(dayString ?? "", 10);
    if (Number.isNaN(year) ||
        Number.isNaN(month) ||
        Number.isNaN(day) ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31) {
        return new Date(Number.NaN);
    }
    return new Date(year, month - 1, day);
}
export function formatDateFromLocalParts(date) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");
    return `${year}-${month}-${day}`;
}
export function parseArgentinaDateAtStartOfDay(dateStr) {
    if (!isValidUrlDate(dateStr)) {
        throw new Error(`Invalid Argentina date: ${dateStr}`);
    }
    return fromZonedTime(`${dateStr}T00:00:00`, ARGENTINA_TZ);
}
function extractIsoDatePrefix(value) {
    const match = /^(\d{4}-\d{2}-\d{2})(?:$|[T ])/.exec(value);
    return match?.[1] ?? null;
}
export function parseArgentinaBusinessDateTime(value) {
    const trimmedValue = value.trim();
    const isoDatePrefix = extractIsoDatePrefix(trimmedValue);
    if (isoDatePrefix && !isValidUrlDate(isoDatePrefix)) {
        throw new Error(`Invalid Argentina datetime: ${value}`);
    }
    if (isValidUrlDate(trimmedValue)) {
        return parseArgentinaDateAtStartOfDay(trimmedValue);
    }
    const utcMidnightMatch = UTC_MIDNIGHT_TIMESTAMP_REGEX.exec(trimmedValue);
    if (utcMidnightMatch?.[1]) {
        return parseArgentinaDateAtStartOfDay(utcMidnightMatch[1]);
    }
    const argentinaLocalDateTimeMatch = ARGENTINA_LOCAL_DATETIME_REGEX.exec(trimmedValue);
    if (argentinaLocalDateTimeMatch?.[1] && argentinaLocalDateTimeMatch?.[2]) {
        const parsedDate = fromZonedTime(`${argentinaLocalDateTimeMatch[1]}T${argentinaLocalDateTimeMatch[2]}`, ARGENTINA_TZ);
        if (Number.isNaN(parsedDate.getTime())) {
            throw new Error(`Invalid Argentina datetime: ${value}`);
        }
        return parsedDate;
    }
    const parsedDate = new Date(trimmedValue);
    if (Number.isNaN(parsedDate.getTime())) {
        throw new Error(`Invalid Argentina datetime: ${value}`);
    }
    return parsedDate;
}
export function isValidUrlDate(dateStr) {
    if (!dateStr || !URL_DATE_REGEX.test(dateStr)) {
        return false;
    }
    const [yearString, monthString, dayString] = dateStr.split("-");
    const year = Number.parseInt(yearString ?? "", 10);
    const month = Number.parseInt(monthString ?? "", 10);
    const day = Number.parseInt(dayString ?? "", 10);
    if (Number.isNaN(year) ||
        Number.isNaN(month) ||
        Number.isNaN(day) ||
        month < 1 ||
        month > 12 ||
        day < 1 ||
        day > 31) {
        return false;
    }
    const parsedDate = new Date(Date.UTC(year, month - 1, day));
    return (parsedDate.getUTCFullYear() === year &&
        parsedDate.getUTCMonth() === month - 1 &&
        parsedDate.getUTCDate() === day);
}
export function formatArgentinaDateOnly(value) {
    if (typeof value === "string") {
        const trimmedValue = value.trim();
        if (isValidUrlDate(trimmedValue)) {
            return trimmedValue;
        }
        const isoDatePrefix = extractIsoDatePrefix(trimmedValue);
        if (isoDatePrefix && !isValidUrlDate(isoDatePrefix)) {
            throw new Error(`Invalid Argentina date: ${value}`);
        }
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        throw new Error(`Invalid Argentina date: ${value}`);
    }
    return formatDateForUrl(date);
}
export function tryFormatArgentinaDateOnly(value) {
    if (value === null || value === undefined || value === "") {
        return;
    }
    try {
        return formatArgentinaDateOnly(value);
    }
    catch {
        return;
    }
}
function extractValidDateOnly(value) {
    const trimmedValue = value.trim();
    if (isValidUrlDate(trimmedValue)) {
        return trimmedValue;
    }
    const isoDatePrefix = extractIsoDatePrefix(trimmedValue);
    return isoDatePrefix && isValidUrlDate(isoDatePrefix) ? isoDatePrefix : null;
}
export function formatArgentinaDateOnlyLabel(value, options) {
    const dateOnly = extractValidDateOnly(value);
    if (!dateOnly) {
        return value || "";
    }
    if (!options) {
        return formatAR(parseArgentinaDateAtStartOfDay(dateOnly), "dd/MM/yyyy");
    }
    const { referenceDate } = options;
    const diffDays = getArgentinaDayNumber(referenceDate) - getArgentinaDayNumber(dateOnly);
    if (diffDays === 0) {
        return "hoy";
    }
    if (diffDays === 1) {
        return "ayer";
    }
    const targetParts = parseDateParts(dateOnly);
    const referenceParts = getArgentinaDateParts(referenceDate);
    const targetDate = parseArgentinaDateAtStartOfDay(dateOnly);
    return targetParts.year === referenceParts.year
        ? formatAR(targetDate, "d MMM")
        : formatAR(targetDate, "dd/MM/yyyy");
}
/** Absolute ISO weeks are labelled by their Monday, independent of host timezone. */
export function formatArgentinaWeekLabel(value) {
    const match = /^(\d{4})-(\d{2})$/.exec(value);
    if (!match)
        return value;
    const year = Number(match[1]);
    const week = Number(match[2]);
    if (week < 1 || week > 53)
        return value;
    const monday = new Date(Date.UTC(year, 0, 4, 12));
    monday.setUTCDate(monday.getUTCDate() - ((monday.getUTCDay() + 6) % 7) + (week - 1) * 7);
    const thursday = new Date(monday);
    thursday.setUTCDate(thursday.getUTCDate() + 3);
    if (thursday.getUTCFullYear() !== year)
        return value;
    return formatAR(monday, "d MMM yyyy");
}
export function formatArgentinaYearMonthLabel(yearMonth) {
    if (!/^\d{4}-\d{2}(-\d{2})?$/.test(yearMonth)) {
        return yearMonth;
    }
    const [yearString, monthString] = yearMonth.split("-");
    const year = Number.parseInt(yearString ?? "", 10);
    const month = Number.parseInt(monthString ?? "", 10);
    if (Number.isNaN(year) || Number.isNaN(month) || month < 1 || month > 12) {
        return yearMonth;
    }
    return formatAR(parseArgentinaDateAtStartOfDay(formatDateParts({ year, month, day: 1 })), "MMM yyyy");
}
export function formatArgentinaMonthName(monthNum) {
    const month = Number.parseInt(monthNum, 10);
    if (Number.isNaN(month) || month < 1 || month > 12) {
        return monthNum;
    }
    return formatAR(parseArgentinaDateAtStartOfDay(formatDateParts({ year: 2000, month, day: 1 })), "MMMM");
}
export function formatAR(date, formatStr) {
    return formatInTimeZone(date, ARGENTINA_TZ, formatStr, { locale: es });
}
export function formatDateAR(date) {
    return formatInTimeZone(date, ARGENTINA_TZ, "dd/MM/yyyy", { locale: es });
}
export function formatDateTimeAR(date) {
    return formatInTimeZone(date, ARGENTINA_TZ, "dd/MM/yyyy HH:mm", {
        locale: es,
    });
}
export function formatTimeAR(date) {
    return formatInTimeZone(date, ARGENTINA_TZ, "HH:mm", { locale: es });
}
/**
 * ISO-8601 timestamp in Argentina time, with the offset written out:
 * `2026-09-07T23:00:00-03:00`. Use it for every timestamp a model or an
 * operator-facing agent payload reads. `Date.prototype.toISOString()` gives
 * UTC, which makes a 23:00 local message read as 02:00 the next day.
 * The offset comes from the IANA zone, so a future DST rule change is handled.
 * Never use it for cursors, SQL comparisons, fingerprints, or stored values.
 */
export function toArgentinaIsoString(date) {
    return formatInTimeZone(date, ARGENTINA_TZ, "yyyy-MM-dd'T'HH:mm:ssXXX");
}
export function toArgentinaTime(date) {
    return toZonedTime(date, ARGENTINA_TZ);
}
export function isTrialActive(trialEndsAt, referenceTime) {
    if (!trialEndsAt) {
        return false;
    }
    const trialEndDate = new Date(trialEndsAt);
    if (Number.isNaN(trialEndDate.getTime())) {
        return false;
    }
    if (isUtcMidnight(trialEndDate)) {
        const trialEndDayNumber = getDayNumberInTimeZone(trialEndDate, UTC_TZ);
        const todayDayNumber = getDayNumberInTimeZone(referenceTime, ARGENTINA_TZ);
        return todayDayNumber <= trialEndDayNumber;
    }
    return trialEndDate > referenceTime;
}
export function getTrialDaysRemaining(trialEndsAt, subscriptionStatus, referenceTime) {
    if (!trialEndsAt) {
        return null;
    }
    if (subscriptionStatus === "active" || subscriptionStatus === "past_due") {
        return null;
    }
    const trialEndDate = new Date(trialEndsAt);
    if (Number.isNaN(trialEndDate.getTime())) {
        return null;
    }
    const trialEndDayNumber = getDayNumberInTimeZone(trialEndDate, isUtcMidnight(trialEndDate) ? UTC_TZ : ARGENTINA_TZ);
    const todayDayNumber = getDayNumberInTimeZone(referenceTime, ARGENTINA_TZ);
    return trialEndDayNumber - todayDayNumber;
}
export function getTodayLocalDate(referenceTime) {
    return formatDateForUrl(referenceTime);
}
export function formatLocalDate(date) {
    return formatDateForUrl(date);
}
export function getArgentinaDateString(date) {
    return formatDateForUrl(date);
}
export function formatDateSafe(date) {
    if (!date) {
        return "";
    }
    if (typeof date === "string") {
        const dateOnlyMatch = DATE_ONLY_REGEX.exec(date) ?? COMPACT_DATE_ONLY_REGEX.exec(date);
        if (dateOnlyMatch) {
            const yearRaw = dateOnlyMatch[1];
            const monthRaw = dateOnlyMatch[2];
            const dayRaw = dateOnlyMatch[3];
            if (!yearRaw || !monthRaw || !dayRaw) {
                return "";
            }
            const year = Number.parseInt(yearRaw, 10);
            const month = Number.parseInt(monthRaw, 10);
            const day = Number.parseInt(dayRaw, 10);
            if (Number.isNaN(year) ||
                Number.isNaN(month) ||
                Number.isNaN(day) ||
                month < 1 ||
                month > 12 ||
                day < 1 ||
                day > 31) {
                return "";
            }
            const targetDate = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
            return targetDate.toLocaleDateString("es-AR", {
                day: "2-digit",
                month: "2-digit",
                timeZone: ARGENTINA_TZ,
                year: "numeric",
            });
        }
        const targetDate = new Date(date);
        if (Number.isNaN(targetDate.getTime())) {
            return "";
        }
        return targetDate.toLocaleDateString("es-AR", {
            day: "2-digit",
            month: "2-digit",
            timeZone: isUtcMidnight(targetDate) ? UTC_TZ : ARGENTINA_TZ,
            year: "numeric",
        });
    }
    if (Number.isNaN(date.getTime())) {
        return "";
    }
    return date.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        timeZone: isUtcMidnight(date) ? UTC_TZ : ARGENTINA_TZ,
        year: "numeric",
    });
}
export function formatRelativeDateTime(timestamp, referenceTime) {
    const targetDate = new Date(timestamp);
    if (!referenceTime) {
        return targetDate.toLocaleDateString("es-AR", {
            day: "2-digit",
            month: "2-digit",
            timeZone: ARGENTINA_TZ,
            year: "numeric",
        });
    }
    const now = referenceTime;
    const diffMs = now.getTime() - targetDate.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    if (diffMinutes < 1) {
        return "recién";
    }
    if (diffMinutes < 10) {
        return `hace ${diffMinutes} min`;
    }
    const dateFormatter = new Intl.DateTimeFormat("es-AR", {
        timeZone: ARGENTINA_TZ,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
    const nowDateStr = dateFormatter.format(now);
    const targetDateStr = dateFormatter.format(targetDate);
    const [nowDay = 0, nowMonth = 0, nowYear = 0] = nowDateStr
        .split("/")
        .map(Number);
    const [targetDay = 0, targetMonth = 0, targetYear = 0] = targetDateStr
        .split("/")
        .map(Number);
    const nowDateOnly = new Date(nowYear, nowMonth - 1, nowDay);
    const targetDateOnly = new Date(targetYear, targetMonth - 1, targetDay);
    const daysDiff = Math.floor((nowDateOnly.getTime() - targetDateOnly.getTime()) / DAY_IN_MS);
    const timeFormat = targetDate.toLocaleTimeString("es-AR", {
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
        hour12: false,
        timeZone: ARGENTINA_TZ,
    });
    if (daysDiff === 0) {
        return timeFormat;
    }
    if (daysDiff === 1) {
        return `ayer a las ${timeFormat}`;
    }
    if (daysDiff < 7) {
        const dayNames = [
            "domingo",
            "lunes",
            "martes",
            "miércoles",
            "jueves",
            "viernes",
            "sábado",
        ];
        const dayOfWeek = new Intl.DateTimeFormat("en-US", {
            timeZone: ARGENTINA_TZ,
            weekday: "short",
        }).format(targetDate);
        const dayIndex = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(dayOfWeek);
        const dayName = dayNames[dayIndex];
        return `${dayName} a las ${timeFormat}`;
    }
    if (targetYear === nowYear) {
        return targetDate.toLocaleDateString("es-AR", {
            day: "numeric",
            month: "short",
            timeZone: ARGENTINA_TZ,
        });
    }
    return targetDate.toLocaleDateString("es-AR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: ARGENTINA_TZ,
    });
}
export function shouldShowElInDate(date, referenceTime) {
    if (!date) {
        return false;
    }
    const targetDate = new Date(date);
    const diffMs = referenceTime.getTime() - targetDate.getTime();
    return diffMs > 4 * DAY_IN_MS;
}
export function getBillingPeriodStart(subscriptionStartedAt, referenceDate) {
    const ref = new Date(referenceDate.toLocaleString("en-US", {
        timeZone: ARGENTINA_TZ,
    }));
    const billingDay = subscriptionStartedAt.getUTCDate();
    const refYear = ref.getFullYear();
    const refMonth = ref.getMonth();
    const refDay = ref.getDate();
    let startYear;
    let startMonth;
    if (refDay >= billingDay) {
        startYear = refYear;
        startMonth = refMonth;
    }
    else if (refMonth === 0) {
        startYear = refYear - 1;
        startMonth = 11;
    }
    else {
        startYear = refYear;
        startMonth = refMonth - 1;
    }
    const daysInStartMonth = new Date(startYear, startMonth + 1, 0).getDate();
    const clampedDay = Math.min(billingDay, daysInStartMonth);
    const year = String(startYear);
    const month = String(startMonth + 1).padStart(2, "0");
    const day = String(clampedDay).padStart(2, "0");
    return `${year}-${month}-${day}`;
}
